import { normalizeAvatarColor } from "./avatarPalette";

export const TEAM_SECURITY_LEVEL_OPTIONS = [
  { value: "owner", label: "Owner" },
  { value: "admin", label: "Admin" },
  { value: "manager", label: "Manager" },
  { value: "member", label: "Member" },
  { value: "viewer", label: "Viewer" },
  { value: "custom", label: "Custom" },
];

export const DEFAULT_TEAM_SECURITY_LEVEL = "member";
export const TEAM_MEMBER_PORTRAIT_OPTIONS = [
  { value: "man", label: "Man" },
  { value: "woman", label: "Woman" },
];
export const DEFAULT_TEAM_MEMBER_PORTRAIT = "man";

export const ORG_CHART_DIMENSIONS = {
  width: 1320,
  height: 820,
  workspaceWidth: 4800,
  workspaceHeight: 3600,
  nodeWidth: 240,
  nodeHeight: 206,
  compactNodeWidth: 226,
  compactNodeHeight: 194,
  paddingX: 28,
  paddingY: 28,
  columnGap: 30,
  rowGap: 36,
  compactColumnGap: 24,
  compactRowGap: 28,
  defaultColumns: 5,
  compactColumns: 6,
  compactThreshold: 18,
};

function createMemberId() {
  if (typeof crypto !== "undefined" && crypto?.randomUUID) {
    return crypto.randomUUID();
  }
  return `member-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function toFiniteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function getMemberSortKey(member = {}) {
  return String(member?.name || member?.email || "Unnamed teammate")
    .trim()
    .toLowerCase();
}

function cleanEmail(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function uniqueEmails(values = []) {
  const out = [];
  const seen = new Set();
  for (const value of values || []) {
    if (Array.isArray(value)) {
      for (const nested of uniqueEmails(value)) {
        if (!seen.has(nested)) {
          seen.add(nested);
          out.push(nested);
        }
      }
      continue;
    }
    const email = cleanEmail(value);
    if (!email || !email.includes("@") || seen.has(email)) continue;
    seen.add(email);
    out.push(email);
  }
  return out;
}

export function getOrgChartLayoutMetrics(memberCount = 0) {
  const compact = Number(memberCount) >= ORG_CHART_DIMENSIONS.compactThreshold;
  return {
    minWidth: compact ? 860 : 720,
    minHeight: compact ? 460 : 380,
    nodeWidth: compact
      ? ORG_CHART_DIMENSIONS.compactNodeWidth
      : ORG_CHART_DIMENSIONS.nodeWidth,
    nodeHeight: compact
      ? ORG_CHART_DIMENSIONS.compactNodeHeight
      : ORG_CHART_DIMENSIONS.nodeHeight,
    paddingX: ORG_CHART_DIMENSIONS.paddingX,
    paddingY: ORG_CHART_DIMENSIONS.paddingY,
    columnGap: compact
      ? ORG_CHART_DIMENSIONS.compactColumnGap
      : ORG_CHART_DIMENSIONS.columnGap,
    rowGap: compact
      ? ORG_CHART_DIMENSIONS.compactRowGap
      : ORG_CHART_DIMENSIONS.rowGap,
    defaultColumns: compact
      ? ORG_CHART_DIMENSIONS.compactColumns
      : ORG_CHART_DIMENSIONS.defaultColumns,
    // Diameter of the round initials badge (components/Avatar.js) on a card.
    avatarSize: compact ? 46 : 52,
  };
}

export function getDefaultOrgChartPosition(index = 0, memberCount = 1) {
  const metrics = getOrgChartLayoutMetrics(memberCount);
  const col = index % metrics.defaultColumns;
  const row = Math.floor(index / metrics.defaultColumns);
  return {
    x: metrics.paddingX + col * (metrics.nodeWidth + metrics.columnGap),
    y: metrics.paddingY + row * (metrics.nodeHeight + metrics.rowGap),
  };
}

function clampOrgChartPosition(value = {}, index = 0, memberCount = 1) {
  const fallback = getDefaultOrgChartPosition(index, memberCount);
  const metrics = getOrgChartLayoutMetrics(memberCount);
  const maxX =
    ORG_CHART_DIMENSIONS.workspaceWidth - metrics.nodeWidth - metrics.paddingX;
  const maxY =
    ORG_CHART_DIMENSIONS.workspaceHeight - metrics.nodeHeight - metrics.paddingY;
  const x = toFiniteNumber(value?.x);
  const y = toFiniteNumber(value?.y);
  return {
    x: Math.max(metrics.paddingX, Math.min(maxX, x ?? fallback.x)),
    y: Math.max(metrics.paddingY, Math.min(maxY, y ?? fallback.y)),
  };
}

export function normalizeTeamSecurityLevel(value) {
  const v = String(value || "")
    .trim()
    .toLowerCase();
  if (!v) return DEFAULT_TEAM_SECURITY_LEVEL;
  if (TEAM_SECURITY_LEVEL_OPTIONS.some((opt) => opt.value === v)) return v;
  return "custom";
}

export function getTeamSecurityLabel(member = {}) {
  const normalized = normalizeTeamSecurityLevel(
    member?.securityLevel || member?.accessLevel || member?.permissionLevel,
  );
  if (normalized === "custom") {
    return String(member?.customSecurityLabel || "Custom").trim() || "Custom";
  }
  return (
    TEAM_SECURITY_LEVEL_OPTIONS.find((opt) => opt.value === normalized)?.label ||
    "Member"
  );
}

export function normalizeTeamPortraitStyle(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  return TEAM_MEMBER_PORTRAIT_OPTIONS.some(
    (option) => option.value === normalized,
  )
    ? normalized
    : DEFAULT_TEAM_MEMBER_PORTRAIT;
}

export function createTeamMember(defaultTZ = "", index = 0, memberCount = 1) {
  return {
    id: createMemberId(),
    name: "",
    email: "",
    role: "",
    securityLevel: DEFAULT_TEAM_SECURITY_LEVEL,
    customSecurityLabel: "",
    portraitStyle: DEFAULT_TEAM_MEMBER_PORTRAIT,
    tz: defaultTZ || "",
    include: true,
    execRecipient: false,
    reportsTo: [],
    orgChart: getDefaultOrgChartPosition(index, memberCount),
  };
}

// GitHub usernames: 1-39 letters, digits or single hyphens, no leading hyphen.
// The server re-validates at read time (integrations/workSignals.js).
const GITHUB_LOGIN_RE = /^[A-Za-z0-9](?:-?[A-Za-z0-9]){0,38}$/;

export function cleanGithubLogin(value) {
  const login = String(value || "")
    .trim()
    .replace(/^@/, "");
  return GITHUB_LOGIN_RE.test(login) ? login : "";
}

// A real photo for the avatar, when an import or a future profile field
// supplies one. Only http(s), so a stray value can never become a
// javascript: or file: source. Left off entirely (not "") when there is none,
// so a directory without photos saves exactly the fields it did before.
function cleanPhotoUrl(value) {
  const url = String(value || "").trim();
  return /^https?:\/\//i.test(url) && url.length <= 2048 ? url : "";
}

export function sanitizeTeamMember(raw = {}, index = 0, memberCount = 1) {
  const reportsTo = Array.isArray(raw?.reportsTo)
    ? raw.reportsTo
    : raw?.managerId
      ? [raw.managerId]
      : [];
  const email = cleanEmail(raw?.email);
  const emailAliases = uniqueEmails([
    raw?.emailAliases,
    raw?.previousEmails,
    raw?.aliases,
    raw?.alternateEmails,
    raw?.previousEmail,
    raw?.oldEmail,
  ]).filter((candidate) => candidate !== email);
  const photoUrl = cleanPhotoUrl(
    raw?.photoUrl || raw?.avatarUrl || raw?.picture || raw?.imageUrl,
  );
  // The badge color picked in Edit teammate: a palette key from
  // lib/avatarPalette.js. Anything else means automatic, and automatic is
  // left off entirely (like photoUrl), so a directory nobody customised saves
  // exactly the fields it did before.
  const avatarColor = normalizeAvatarColor(raw?.avatarColor);

  return {
    id: String(raw?.id || createMemberId()),
    name: String(raw?.name || ""),
    email,
    emailAliases,
    role: String(raw?.role || ""),
    securityLevel: normalizeTeamSecurityLevel(
      raw?.securityLevel || raw?.accessLevel || raw?.permissionLevel,
    ),
    customSecurityLabel: String(
      raw?.customSecurityLabel || raw?.securityLabel || "",
    ),
    portraitStyle: normalizeTeamPortraitStyle(
      raw?.portraitStyle || raw?.avatarStyle || raw?.imageStyle,
    ),
    ...(photoUrl ? { photoUrl } : {}),
    ...(avatarColor ? { avatarColor } : {}),
    tz: String(raw?.tz || ""),
    // Matches PR authors and reviewers to this person for pings and
    // escalations. Optional.
    githubLogin: cleanGithubLogin(raw?.githubLogin),
    include: raw?.include !== false,
    execRecipient: !!raw?.execRecipient,
    reportsTo: reportsTo
      .map((id) => String(id || "").trim())
      .filter(Boolean),
    orgChart: clampOrgChartPosition(
      raw?.orgChart || {
        x: raw?.orgChartX,
        y: raw?.orgChartY,
      },
      index,
      memberCount,
    ),
  };
}

export function sanitizeTeamMembers(input = []) {
  const arr = Array.isArray(input) ? input : [];
  const members = arr.map((member, index) =>
    sanitizeTeamMember(member, index, arr.length),
  );
  const validIds = new Set(members.map((member) => member.id));

  return members.map((member, index) => ({
    ...member,
    reportsTo: member.reportsTo.filter(
      (id, idx, source) =>
        id &&
        id !== member.id &&
        validIds.has(id) &&
        source.indexOf(id) === idx,
    ),
    orgChart: clampOrgChartPosition(member.orgChart, index, members.length),
  }));
}

export function getDirectReports(memberId, teamMembers = []) {
  return (teamMembers || []).filter((member) =>
    Array.isArray(member?.reportsTo) ? member.reportsTo.includes(memberId) : false,
  );
}

export function buildOrgChartAutoLayout(teamMembers = []) {
  const members = Array.isArray(teamMembers) ? teamMembers : [];
  if (!members.length) return {};

  const metrics = getOrgChartLayoutMetrics(members.length);
  const byId = new Map(members.map((member) => [member.id, member]));
  const primaryManagerById = new Map();
  const childrenById = new Map(members.map((member) => [member.id, []]));
  const assignedChildren = new Set();

  members.forEach((member) => {
    const managerId = (Array.isArray(member.reportsTo) ? member.reportsTo : [])
      .map((id) => String(id || "").trim())
      .find((id) => id && id !== member.id && byId.has(id));
    primaryManagerById.set(member.id, managerId || null);
    if (managerId) {
      childrenById.get(managerId)?.push(member);
      assignedChildren.add(member.id);
    }
  });

  childrenById.forEach((children) => {
    children.sort((left, right) =>
      getMemberSortKey(left).localeCompare(getMemberSortKey(right)),
    );
  });

  const roots = members
    .filter((member) => !assignedChildren.has(member.id))
    .sort((left, right) =>
      getMemberSortKey(left).localeCompare(getMemberSortKey(right)),
    );

  const orderedRoots = roots.length
    ? roots
    : [...members].sort((left, right) =>
        getMemberSortKey(left).localeCompare(getMemberSortKey(right)),
      );

  const subtreeWidthById = new Map();
  const visitedForWidth = new Set();

  function getSubtreeWidth(memberId) {
    if (subtreeWidthById.has(memberId)) return subtreeWidthById.get(memberId);
    if (visitedForWidth.has(memberId)) return metrics.nodeWidth;
    visitedForWidth.add(memberId);
    const children = childrenById.get(memberId) || [];
    const childrenWidth = children.reduce((sum, child, index) => {
      const childWidth = getSubtreeWidth(child.id);
      return sum + childWidth + (index ? metrics.columnGap : 0);
    }, 0);
    const width = Math.max(metrics.nodeWidth, childrenWidth);
    visitedForWidth.delete(memberId);
    subtreeWidthById.set(memberId, width);
    return width;
  }

  const positions = {};
  const visitedForPlacement = new Set();

  function placeTree(member, leftX, depth) {
    if (visitedForPlacement.has(member.id)) {
      return leftX + metrics.nodeWidth / 2;
    }
    visitedForPlacement.add(member.id);

    const subtreeWidth = getSubtreeWidth(member.id);
    const children = childrenById.get(member.id) || [];
    const y = Math.round(
      metrics.paddingY + depth * (metrics.nodeHeight + metrics.rowGap),
    );

    if (!children.length) {
      const x = Math.round(leftX + (subtreeWidth - metrics.nodeWidth) / 2);
      positions[member.id] = { x, y };
      return x + metrics.nodeWidth / 2;
    }

    let cursorX = leftX;
    const childCenters = children.map((child) => {
      const center = placeTree(child, cursorX, depth + 1);
      cursorX += getSubtreeWidth(child.id) + metrics.columnGap;
      return center;
    });

    const firstCenter = childCenters[0];
    const lastCenter = childCenters[childCenters.length - 1];
    const centerX = (firstCenter + lastCenter) / 2;
    const minX = leftX;
    const maxX = leftX + subtreeWidth - metrics.nodeWidth;
    const x = Math.round(
      Math.max(minX, Math.min(maxX, centerX - metrics.nodeWidth / 2)),
    );
    positions[member.id] = { x, y };
    return x + metrics.nodeWidth / 2;
  }

  const totalTreeWidth = orderedRoots.reduce((sum, root, index) => {
    const width = getSubtreeWidth(root.id);
    const gap = index < orderedRoots.length - 1 ? metrics.columnGap * 2 : 0;
    return sum + width + gap;
  }, 0);
  const availableInnerWidth = Math.max(
    metrics.minWidth - metrics.paddingX * 2,
    totalTreeWidth,
  );
  let cursorX =
    metrics.paddingX + Math.max(0, (availableInnerWidth - totalTreeWidth) / 2);

  orderedRoots.forEach((root, index) => {
    placeTree(root, cursorX, 0);
    cursorX += getSubtreeWidth(root.id);
    if (index < orderedRoots.length - 1) {
      cursorX += metrics.columnGap * 2;
    }
  });

  members.forEach((member) => {
    if (!positions[member.id]) {
      const x = Math.round(cursorX);
      const y = metrics.paddingY;
      positions[member.id] = { x, y };
      cursorX += metrics.nodeWidth + metrics.columnGap;
    }
  });

  return positions;
}
